/**
 * TenantStatusBanner — sticky dunning ribbon shown on every /admin page.
 *
 * Polls `/api/admin/billing/health` every 10 minutes (and on every route
 * change) and renders a colour-coded ribbon based on `severity`:
 *
 *   • blocked  → red, account SUSPENDED — full-bleed CTA
 *   • critical → orange, GRACE / expired — Renew + WhatsApp invite
 *   • warning  → amber, trial / auto-renew imminent
 *   • ok       → hidden
 *
 * Renders nothing for non-admin roles, during impersonation (super admin
 * already sees the platform-level data), and on any transient error.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, MessageCircle, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const COLOURS = {
    blocked: "bg-red-600 text-white",
    critical: "bg-orange-500 text-white",
    warning: "bg-amber-400 text-amber-950",
};

export default function TenantStatusBanner() {
    const { user } = useAuth();
    const loc = useLocation();
    const [h, setH] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        // Only tenant-admin contexts get the dunning banner.
        if (!user || user.role !== "TENANT_ADMIN") return;
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/admin/billing/health");
                if (!cancelled) setH(data);
            } catch {
                if (!cancelled) setH(null);
            }
        };
        load();
        // Reset the dismissed state when route changes so the user is
        // re-nudged after navigation.
        setDismissed(false);
        const t = setInterval(load, 10 * 60 * 1000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user, loc.pathname]);

    if (!user || user.role !== "TENANT_ADMIN") return null;
    if (!h || h.severity === "ok" || dismissed) return null;

    const cls = COLOURS[h.severity] || COLOURS.warning;
    const isBlocked = h.severity === "blocked";

    return (
        <div data-testid="tenant-status-banner" className={`${cls} px-4 py-2.5 sticky top-0 z-40 shadow-sm`}>
            {/* Bug found on mobile: the headline `<p>` used to sit as a
                `flex-1 min-w-0` sibling of the fixed-width button group in
                ONE flex row. A flex-1 item's hypothetical size collapses to
                0 with `min-w-0`, so the row never satisfies `flex-wrap`'s
                overflow condition — the browser just shrinks the paragraph's
                column down to a sliver instead of wrapping the row, and at
                that point even `overflow-wrap: break-word` (the correct,
                already-fixed global primitive — see index.css) has to break
                mid-word because no whole word fits the column at all.
                Fixed by stacking headline/buttons as separate flex rows on
                mobile (`flex-col`) so the headline gets the FULL width to
                wrap normally, reverting to one row from `sm:` (640px) up
                where there's room — unchanged from before at that size. */}
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-sm">
                <div className="flex items-start gap-2 min-w-0 sm:flex-1">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5 sm:mt-0" />
                    <p className="font-bold break-words" data-testid="tenant-banner-headline">{h.headline}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:shrink-0">
                    <Link
                        to={h.renew_url || "/admin/billing"}
                        data-testid="tenant-banner-renew"
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${isBlocked ? "bg-white text-red-700" : "bg-black/15 hover:bg-black/25"}`}
                    >
                        Renew now
                    </Link>
                    <a
                        href={h.wa_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="tenant-banner-wa"
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${isBlocked ? "bg-white text-emerald-700" : "bg-black/15 hover:bg-black/25"}`}
                    >
                        <MessageCircle size={12} /> WhatsApp invite
                    </a>
                    {!isBlocked && (
                        <button data-testid="tenant-banner-dismiss" onClick={() => setDismissed(true)} className="opacity-80 hover:opacity-100 p-1 -mr-1 shrink-0" aria-label="Dismiss">
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
